import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import DonutChart from "../components/donutChart";
import { useTheme } from "next-themes";
import Modal from "bootstrap/js/dist/modal";
import { amountToWords, format2, format4, numberWithCommas, separateDecimal, separateNumber, separateNumberParts } from "../utils/numbers";
import { tabButton, themeBasedPurchaseFormControl } from "../components/customStyles";
import { useAuth } from "../store/AuthContext";
import toast from "react-hot-toast";
import { customToast } from "../components/toasts";
import { addNewInvestor } from "../services/invest.service";
import { BankAccountCard } from "../components/bankCard";
import { useTranslation } from "react-i18next";
import { useI18n } from "../hooks/useI18n";
import { Tooltip } from "react-tooltip";
import useWindowSize from "../hooks/useWindowSize";
import FundSelector from "../components/fundSelector";
import { useNavigate, useSearchParams } from "react-router-dom";

function groupByCategory(items = [], opts = { normalize: true }) {
  const { normalize } = opts;
  const groups = new Map();

  for (const item of items) {
    let key = item?.nature;
    if (normalize) {
      key = typeof key === "string" ? key.trim() : key;
    }
    const name = key && key !== "" ? key : "Unspecified";

    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(item);
  }

  // return as array preserving order of first appearance
  return Array.from(groups.entries()).map(([name, items]) => ({
    name,
    items,
    count: items.length,
  }));
}

export default function InvestorPortfolio({ unitholder, loadingUnitholder, funds, portfolioFunds }) {
  const [viewChartMode, setViewChartMode] = useState("fund");
  const [validationError, setValidationError] = useState(false);
  const [activeRedeemFund, setActiveRedeemFund] = useState(null);
  const [redeemData, setRedeemData] = useState(() => ({
    fund_units: "",
    total_amount: "",
  }));
  const [conversionData, setConversionData] = useState(() => ({
    fund_units: "",
    total_amount: "",
    category: { name: "All" },
    fund: null,
    search: "",
    calculated_units: "",
  }));
  const [fundPaymentData, setFundPaymentData] = useState(() => ({
    bank_name: "",
    branch_name: "",
    account_title: "",
    iban: "",
    payment_method: "",
    comment: "",
    payment_proof_file: null,
  }));
  const [loading, setLoading] = useState(() => ({
    addPortfolioModal: false,
    redeemFund: false,
    paymentFund: false,
    finalPaymentFund: false,
    deletePortfolio: false,
    transactionHistory: false,
    convertUnits: false,
  }));

  const { theme } = useTheme();

  const { t } = useTranslation();

  const screenSize = useWindowSize();

  const { isRTL } = useI18n();

  const { user } = useAuth();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  // const searchTerm = searchParams.get("search");
  // const searchQuery = searchParams.get("query");
  const purchaseSearchModal = searchParams.get("modal");
  const purchaseSearchSymbol = searchParams.get("symbol");

  const derivedFundsRef = useRef(null);
  const redeemProcess = useRef("");

  const conversionModalRef = useRef(null);
  const conversionModalInstanceRef = useRef(null);

  const redeemModalRef = useRef(null);
  const redeemModalInstanceRef = useRef(null);

  const paymentModal = useRef(null);
  const paymentModalInstance = useRef(null);

  const hasHoldings = useMemo(() => {
    if (!unitholder) return false;

    if (!unitholder.balance_unit) return false;

    const holdings = JSON.parse(unitholder.balance_unit || "[]");

    if (holdings.length === 0) return false;

    return true;
  }, [unitholder]);

  // const portfolioFunds = useMemo(() => {
  // if (!hasHoldings) return null;

  // return JSON.parse(unitholder.balance_unit);
  // }, [hasHoldings])

  const conversionSelectFunds = useMemo(() => {
    const category = conversionData?.category?.name ?? "All";
    const searchTerm = (conversionData?.search ?? "").trim().toLowerCase();

    let base = Array.isArray(funds) ? funds : [];

    if (category !== "All") {
      base = base.filter((item) => (item?.nature ?? "") === category);
    }

    if (activeRedeemFund) {
      base = base.filter((item) => item.symbol_code !== (activeRedeemFund?.symbol_code || activeRedeemFund?.fund_code));
    }

    if (!searchTerm) return base;

    const matches = (item) => {
      const symbol = String(item?.symbol_code ?? "").toLowerCase();
      const name = String(item?.fund_name ?? "").toLowerCase();
      return symbol.includes(searchTerm) || name.includes(searchTerm);
    };

    return base.filter(matches);
  }, [conversionData.search, conversionData.category, funds]);

  const fundCategories = groupByCategory(funds);

  const getTotalAmount = () => {
    let amount = 0;

    // if (activePortfolioFunds) {
    //   activePortfolioFunds?.forEach((fund) => {
    //     const nav = parseFloat(fund.navData?.nav || 0);
    //     const units = parseFloat(fund.fund_unit);

    //     const calculations = nav * units;

    //     amount += calculations;
    //   });
    // } else {
    portfolioFunds?.forEach((fund) => {
      const nav = parseFloat(fund.navData?.nav || 0);
      const units = parseFloat(fund.fund_unit);

      const calculations = nav * units;

      amount += calculations;
    });
    // }

    return format4(amount);
  };

  const getTotalInvestment = () => {
    let amount = 0;

    // if (activePortfolioFunds) {
    //   activePortfolioFunds?.forEach((fund) => {
    //     const nav = parseFloat(fund?.nav || 0);
    //     const units = parseFloat(fund.fund_unit);

    //     const calculations = nav * units;

    //     amount += calculations;
    //   });
    // } else {
    portfolioFunds?.forEach((fund) => {
      if (fund.investment) {
        const investment = parseFloat(fund.investment || 0);
        amount += investment;

        console.log(investment);

      } else {
        const nav = parseFloat(fund?.nav || 0);
        const units = parseFloat(fund.fund_unit);

        const calculations = nav * units;

        amount += calculations;
      }
    });

    console.log(amount)
    // }

    return format4(amount);
  };

  const getTotalReturn = () => {
    const totalAmount = getTotalAmount();
    const totalInvestment = getTotalInvestment();

    return format2(totalAmount - totalInvestment);
  };

  const toggleRedeemModal = (data) => {
    redeemProcess.current = "start-1";
    setActiveRedeemFund(data);
    redeemModalInstanceRef.current.toggle();
  };

  const toggleConversionModal = (data) => {
    // redeemProcess.current = "start-1";
    setActiveRedeemFund(data);
    // setConversionData(() => ({
    //   fund_units: "",
    //   total_amount: "",
    // }));
    conversionModalInstanceRef.current.toggle();
  };

  const handleRedeemFund = async (e) => {
    e.preventDefault();

    let status = true;

    try {
      setLoading((prev) => ({ ...prev, redeemFund: true }));

      await new Promise((res) => setTimeout(() => res(), 1000));

      if (redeemData.fund_units === "") {
        toast((t) => customToast("Please enter fund units before proceeding", "error", t), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            width: "300px",
            maxWidth: "100%",
            // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
            // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
            color: "rgb(33,37,41)",
            backgroundColor: "rgb(221, 204, 225)",
          },
        });

        status = false;
      }

      if (redeemData.total_amount === "") {
        toast((t) => customToast("Please enter amount before proceeding", "error", t), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            width: "300px",
            maxWidth: "100%",
            // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
            // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
            color: "rgb(33,37,41)",
            backgroundColor: "rgb(221, 204, 225)",
          },
        });

        status = false;
      }

      // const response = await getFundBankAccounts({ fund_code: activeRedeemFund?.symbol_code, email: user.data.email });
    } finally {
      setLoading((prev) => ({ ...prev, redeemFund: false }));
    }

    if (status) {
      if (parseFloat(redeemData.fund_units) > parseFloat(activeRedeemFund.fund_unit)) {
        toast((t) => customToast("Redemption units cannot exceed your available units", "error", t), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            width: "360px",
            maxWidth: "100%",
            // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
            // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
            color: "rgb(33,37,41)",
            backgroundColor: "rgb(221, 204, 225)",
          },
        });

        status = false;

        return;
      }

      togglePaymentModal();
    }
  };

  const handleRedeemFundChange = (e) => {
    const { name, value } = e.target;

    const decimalLimit = name === "total_amount" ? 4 : 4;

    let formatted = "";

    if (value !== "") {
      let hasDecimal = false;
      let decimalCount = 0;

      for (const x of value) {
        if (x === ".") {
          // allow only the first dot
          if (!hasDecimal) {
            formatted += x;
            hasDecimal = true;
          }
        } else if (x >= "0" && x <= "9") {
          if (hasDecimal) {
            // after the dot, only up to 4 digits
            if (decimalCount < decimalLimit) {
              formatted += x;
              decimalCount += 1;
            }
          } else {
            // before the dot, all digits allowed
            formatted += x;
          }
        } else if (x === ",") {
          // keep commas for now (you strip them below)
          formatted += x;
        }
        // everything else is skipped
      }
    }

    formatted = formatted.split(",").join("");

    setRedeemData((prev) => ({ ...prev, [name]: formatted }));

    if (name === "fund_units") {
      const parsed = parseFloat(formatted);

      if (parsed > activeRedeemFund?.fund_unit) {
        setValidationError(true);
      } else {
        setValidationError(false);
      }

      if (!isNaN(parsed)) {
        const calcAmount = format4(parsed * activeRedeemFund?.navData.redemption_price);

        setRedeemData((prev) => ({ ...prev, total_amount: calcAmount }));
      } else {
        setRedeemData((prev) => ({ ...prev, total_amount: "" }));
      }
    } else if (name === "total_amount") {
      const parsed = parseFloat(formatted);

      if (!isNaN(parsed)) {
        const calcUnits = format4(parsed / activeRedeemFund?.navData.redemption_price);

        setRedeemData((prev) => ({ ...prev, fund_units: calcUnits }));
      } else {
        setRedeemData((prev) => ({ ...prev, fund_units: "" }));
      }
    }
  };

  const handleConversionChange = (e) => {
    const { name, value } = e.target;

    const decimalLimit = name === "total_amount" ? 4 : 4;

    let formatted = "";

    if (value !== "") {
      let hasDecimal = false;
      let decimalCount = 0;

      for (const x of value) {
        if (x === ".") {
          // allow only the first dot
          if (!hasDecimal) {
            formatted += x;
            hasDecimal = true;
          }
        } else if (x >= "0" && x <= "9") {
          if (hasDecimal) {
            // after the dot, only up to 4 digits
            if (decimalCount < decimalLimit) {
              formatted += x;
              decimalCount += 1;
            }
          } else {
            // before the dot, all digits allowed
            formatted += x;
          }
        } else if (x === ",") {
          // keep commas for now (you strip them below)
          formatted += x;
        }
        // everything else is skipped
      }
    }

    formatted = formatted.split(",").join("");

    setConversionData((prev) => ({ ...prev, [name]: formatted }));

    if (name === "fund_units") {
      const parsed = parseFloat(formatted);

      if (parsed > activeRedeemFund?.fund_unit) {
        setValidationError(true);
      } else {
        setValidationError(false);
      }

      if (!isNaN(parsed)) {
        const calcAmount = format4(parsed * activeRedeemFund?.navData.redemption_price);

        let temp = "";

        if (conversionData.fund && !(parsed <= 0)) {
          const localValue = parseFloat(calcAmount) / parseFloat(conversionData.fund?.navData?.offer_price);

          temp = format4(localValue);
        }

        setConversionData((prev) => ({ ...prev, total_amount: calcAmount, calculated_units: temp !== "" ? temp : "" }));
      } else {
        setConversionData((prev) => ({ ...prev, total_amount: "" }));
      }
    } else if (name === "total_amount") {
      const parsed = parseFloat(formatted);

      if (!isNaN(parsed)) {
        const calcUnits = format4(parsed / activeRedeemFund?.navData.redemption_price);

        let temp = "";

        if (conversionData.fund && !(parsed <= 0)) {
          const localValue = parsed / parseFloat(conversionData.fund?.navData?.offer_price);

          temp = format4(localValue);
        }

        if (parseFloat(calcUnits) > activeRedeemFund?.fund_unit) {
          setValidationError(true);
        } else {
          setValidationError(false);
        }

        setConversionData((prev) => ({ ...prev, fund_units: calcUnits, calculated_units: temp !== "" ? temp : "" }));
      } else {
        setConversionData((prev) => ({ ...prev, fund_units: "" }));
      }
    }
  };

  const updateFundPaymentData = (e, isBankAccount) => {
    if (isBankAccount) {
      if (e) {
        setFundPaymentData((prev) => ({
          ...prev,
          bank_account: e,
          bank_name: e.bank_name,
          branch_name: e.branch_name,
          account_title: e.account_title,
          iban: e.account_no,
        }));
      } else {
        setFundPaymentData((prev) => ({
          ...prev,
          bank_account: "",
          bank_name: "",
          branch_name: "",
          account_title: "",
          iban: "",
        }));
      }
    } else {
      const { name, value } = e.target;

      setFundPaymentData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFundPayment = async (e) => {
    e.preventDefault();

    let status = true;

    try {
      setLoading((prev) => ({ ...prev, paymentFund: true }));

      await new Promise((res) => setTimeout(() => res(), 1000));

      if (
        !fundPaymentData.bank_name.trim() ||
        !fundPaymentData.branch_name.trim() ||
        !fundPaymentData.account_title.trim() ||
        !fundPaymentData.iban.trim() ||
        !redeemData.fund_units.trim()
      ) {
        toast((t) => customToast("Please fill all the required payment details before submitting", "error", t), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            width: "360px",
            maxWidth: "100%",
            // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
            // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
            color: "rgb(33,37,41)",
            backgroundColor: "rgb(221, 204, 225)",
          },
        });

        status = false;
      }

      if (parseFloat(redeemData.fund_units) > parseFloat(activeRedeemFund.fund_unit)) {
        toast((t) => customToast("Redemption units cannot exceed your available units", "error", t), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            width: "360px",
            maxWidth: "100%",
            // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
            // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
            color: "rgb(33,37,41)",
            backgroundColor: "rgb(221, 204, 225)",
          },
        });

        status = false;
      }

      if (status) {
        const argsData = {
          email: user.data.email,
          fund_code: activeRedeemFund?.fund_code,
          folio_no: user.data.folio_no,
          request_type: "redemptionofunits",
          // payment_method: fundPaymentData.payment_method,
          units_requested: String(redeemData.fund_units),
          gross_amount_limit: String(redeemData.total_amount),
          nav_at_request: activeRedeemFund?.navData?.nav || 0,
          investor_bank_name: fundPaymentData.bank_name,
          investor_branch_name: fundPaymentData.branch_name,
          investor_bank_iban: fundPaymentData.iban,
          investor_comment: fundPaymentData.comment || null,
          investor_account_title: fundPaymentData.account_title,

          // bank_account_code: fundPaymentData.bank_account?.value?.account_code || '',
          // payment_proof_file: fundPaymentData.payment_proof_file || null
        };

        const response = await addNewInvestor(argsData);

        if (!response.error) {
          toast((t) => customToast(response.message, "success", t), {
            duration: 3000,
            removeDelay: 1000,
            style: {
              padding: "0.75rem",
              margin: "0",
              width: "300px",
              maxWidth: "100%",
              // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
              // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
              color: "rgb(33,37,41)",
              backgroundColor: "rgb(221, 204, 225)",
            },
          });

          togglePaymentModal();
        } else {
          toast((t) => customToast(response.message, "error", t), {
            duration: 3000,
            removeDelay: 1000,
            style: {
              padding: "0.75rem",
              margin: "0",
              width: "300px",
              maxWidth: "100%",
              // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
              // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
              color: "rgb(33,37,41)",
              backgroundColor: "rgb(221, 204, 225)",
            },
          });
        }
      }
    } finally {
      setLoading((prev) => ({ ...prev, paymentFund: false }));
    }
  };

  const togglePaymentModal = () => {
    redeemProcess.current = "start-2";

    if (redeemModalRef.current) {
      redeemModalInstanceRef.current.hide();
    }

    paymentModalInstance.current.toggle();
    setFundPaymentData((prev) => ({
      ...prev,
      bank_name: unitholder?.bank_name,
      branch_name: unitholder?.branch_name,
      account_title: unitholder?.account_title,
      iban: unitholder?.account_no,
    }));

    if (paymentModal.current) {
      paymentModalInstance.current.hide();
    }
  };

  useEffect(() => {
    if (loadingUnitholder) return;
    if (!hasHoldings) return;

    const resetRedeemData = () => {
      if (redeemProcess.current !== "start-2") {
        setRedeemData(() => ({
          fund_units: "",
          total_amount: "",
        }));

        setValidationError(false);
      }
    };

    const resetPaymentData = () => {
      setRedeemData(() => ({
        fund_units: "",
        total_amount: "",
      }));

      setFundPaymentData(() => ({
        bank_name: "",
        branch_name: "",
        account_title: "",
        iban: "",
        payment_method: "",
        comment: "",
        payment_proof_file: null,
      }));

      setValidationError(false);

      redeemProcess.current = "";
    };

    const resetConversionModal = () => {
      const currentFund = derivedFundsRef.current && derivedFundsRef.current?.length !== 0 ? derivedFundsRef.current?.[0] : null;

      if (currentFund) {
        setConversionData(() => ({
          fund_units: "",
          total_amount: "",
          category: { name: "All" },
          // fund: currentFund,
          fund: null,
          search: "",
          calculated_units: "",
        }));

        setValidationError(false);

        return;
      }

      setConversionData(() => ({
        fund_units: "",
        total_amount: "",
        category: { name: "All" },
        fund: null,
        search: "",
        calculated_units: "",
      }));

      setValidationError(false);
    };

    if (conversionModalRef.current) {
      conversionModalInstanceRef.current = new Modal(conversionModalRef.current, { backdrop: true, keyboard: true });
    }

    if (paymentModal.current) {
      paymentModalInstance.current = new Modal(paymentModal.current, { backdrop: true, keyboard: false });

      paymentModal.current.addEventListener("hidden.bs.modal", resetPaymentData);
    }

    if (redeemModalRef.current) {
      redeemModalInstanceRef.current = new Modal(redeemModalRef.current, { backdrop: true, keyboard: false });

      redeemModalRef.current.addEventListener("hidden.bs.modal", resetRedeemData);
    }

    conversionModalRef.current?.addEventListener("hidden.bs.modal", resetConversionModal);

    return () => {
      paymentModal.current?.removeEventListener("hidden.bs.modal", resetPaymentData);
      redeemModalRef.current?.removeEventListener("hidden.bs.modal", resetRedeemData);
      conversionModalRef.current?.removeEventListener("hidden.bs.modal", resetConversionModal);

      paymentModalInstance.current?.dispose();
      redeemModalInstanceRef.current?.dispose();
      conversionModalInstanceRef.current?.dispose();
    };
  }, [loadingUnitholder, hasHoldings]);

  useEffect(() => {
    if (portfolioFunds && portfolioFunds?.length !== 0) {
      derivedFundsRef.current = portfolioFunds;
    }
  }, [portfolioFunds]);

  useEffect(() => {
    if (portfolioFunds && portfolioFunds.length !== 0 && purchaseSearchModal) {
      const currentFund = portfolioFunds.find((item) => item.fund_code === purchaseSearchSymbol);

      if (currentFund) {
        navigate("/portfolio", { replace: true });
        if (purchaseSearchModal === 'convert') {
          toggleConversionModal(currentFund);
        } else {
          toggleRedeemModal(currentFund);
        }
      }
    }
  }, [portfolioFunds, purchaseSearchModal]);

  const currentPortfolio = portfolioFunds?.length !== 0 ? separateNumber(getTotalAmount(), { format: true, digits: 4 }) : null;
  const totalInvestment = portfolioFunds?.length !== 0 ? separateNumber(getTotalInvestment(), { format: true, digits: 4 }) : null;
  const totalReturn = portfolioFunds?.length !== 0 ? separateDecimal(getTotalReturn()) : null;
  const unitHoldings = separateNumber(activeRedeemFund?.fund_unit || "0");
  const redemptionPrice = separateNumber(format4(activeRedeemFund?.navData?.redemption_price || "0"));
  const amount = separateNumber(format4(parseFloat(activeRedeemFund?.navData?.redemption_price || 0) * parseFloat(activeRedeemFund?.fund_unit || 0)));

  if (loadingUnitholder) {
    return (
      <div
        className='w-100 position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
        style={{
          color: theme === "light" ? "rgb(0,0,0)" : "rgb(255,255,255)",
        }}
      >
        <div
          className=''
          role='status'
          style={{
            fontSize: "1.25rem",
            fontWeight: "300",
            letterSpacing: isRTL ? "" : ".02em",
          }}
        >
          {t("app.loading.portfolio")}
        </div>
        <div
          className='spinner-border'
          style={{
            fontSize: "11px",
            height: "24px",
            width: "24px",
            color: theme === "light" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
          }}
        >
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  if (!hasHoldings) {
    return (
      <div
        className='w-100 position-absolute start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
        style={{
          color: theme === "light" ? "rgb(0,0,0)" : "rgb(255,255,255)",
          top: "calc(50%)",
        }}
      >
        <div
          className=''
          role='status'
          style={{
            fontSize: "1.2rem",
            color: theme === "dark" ? "rgb(200,200,200)" : "rgb(200,200,200)",
          }}
        >
          No Balance Units Found
        </div>
      </div>
    );
  }

  return (
    <div className="col-12 row w-100 mx-auto flex-column flex-nowrap" style={{
      overflowY: "auto",
      padding: screenSize >= 576 ? '1rem' : '.75rem',
      rowGap: "1rem",
            height: screenSize >= 992
                  ? "calc(100dvh - 192px)"
                  : screenSize <= 575
                    ? "calc(100dvh - 212px)"
                    : "calc(100dvh - 265px)",
      scrollbarWidth: "none",
      borderRadius: screenSize <= 575 ? "20px" : "",
    }}>
      <div className='col-12 p-0'>
        <div className='row w-100 mx-auto' style={{ rowGap: "1rem" }}>
          <div className='col-12 col-lg-6 col-lg-6 p-0 pe-lg-2 animate-card'>
            <div
              className='row w-100 h-100 mx-auto flex-column gap-3'
              style={{
                minHeight: "350px",
              }}
            >
              <div className='col-12 flex-grow-1 p-0'>
                <div className='row w-100 h-100 mx-auto gap-3'>
                  <div className='col-12 col-sm p-0'>
                    <div
                      className='d-flex flex-column align-items-center justify-content-center gap-1 h-100 p-0'
                      style={{
                        lineHeight: "1",
                        // rowGap: '0.45rem',
                        // backgroundColor: 'rgba(255,255,255,0.7)',
                        // backgroundColor: theme === "light" ? "rgba(255,255,255,0.7)" : "rgb(100, 100, 100)",
                        backgroundImage: "linear-gradient(120deg, var(--theme-card-primary-bg) 0%, var(--theme-card-secondary-bg) 100%)",
                        border: "1px solid",
                        borderColor: "var(--theme-card-border)",
                        boxShadow: "rgba(99, 99, 99, 0.15) 0px 2px 8px 2px",
                        borderRadius: "20px",
                        minHeight: "110px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "1.05rem",
                          fontWeight: "500",
                          letterSpacing: ".04em",
                          color: theme === "light" ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.75)",
                        }}
                      >
                        {t("portfolio.card.current")}
                      </div>
                      {currentPortfolio !== null ? (
                        <div
                          dir='ltr'
                          style={{
                            fontSize: "1.75rem",
                            fontWeight: "500",
                            marginTop: "0.36rem",
                            letterSpacing: ".03em",
                            color: theme === "light" ? "rgb(0,0,0)" : "rgb(237, 153, 243)",
                          }}
                        >
                          <span
                            dir='ltr'
                            style={{
                              fontSize: screenSize <= 767 ? "0.7rem" : "1rem",
                              marginRight: isRTL ? ".5rem" : ".25rem",
                              color: theme === "dark" ? "rgb(195, 200, 212)" : "rgb(16 109 221 / 76%)",
                            }}
                          >
                            {t("app.fund.rupee")}
                          </span>
                          {numberWithCommas(currentPortfolio.whole) + "."}
                          <span
                            style={{
                              fontSize: screenSize >= 768 ? ".965rem" : screenSize <= 767 && screenSize >= 576 ? "0.75rem" : ".7rem",
                            }}
                          >
                            {currentPortfolio.decimal}
                          </span>
                        </div>
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                  <div className='col-12 col-sm p-0'>
                    <div
                      className='d-flex flex-column align-items-center justify-content-center gap-1 h-100 p-0'
                      style={{
                        lineHeight: "1",
                        // rowGap: '0.45rem',
                        // backgroundColor: theme === "light" ? "rgba(255,255,255,0.7)" : "rgb(100, 100, 100)",
                        backgroundImage: "linear-gradient(120deg, var(--theme-card-primary-bg) 0%, var(--theme-card-secondary-bg) 100%)",
                        border: "1px solid",
                        borderColor: "var(--theme-card-border)",
                        boxShadow: "rgba(99, 99, 99, 0.15) 0px 2px 8px 2px",
                        borderRadius: "20px",
                        minHeight: "110px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "1.05rem",
                          fontWeight: "500",
                          letterSpacing: ".04em",
                          color: theme === "light" ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.75)",
                        }}
                      >
                        {t("portfolio.card.invest")}
                      </div>
                      {totalInvestment !== null ? (
                        <div
                          dir='ltr'
                          style={{
                            fontSize: "1.75rem",
                            fontWeight: "500",
                            marginTop: "0.36rem",
                            letterSpacing: ".03em",
                            color: theme === "light" ? "rgb(0,0,0)" : "rgb(237, 153, 243)",
                          }}
                        >
                          <span
                            dir='ltr'
                            style={{
                              fontSize: screenSize <= 767 ? "0.7rem" : "1rem",
                              marginRight: isRTL ? ".5rem" : ".25rem",
                              color: theme === "dark" ? "rgb(195, 200, 212)" : "rgb(16 109 221 / 76%)",
                            }}
                          >
                            {t("app.fund.rupee")}
                          </span>
                          {numberWithCommas(totalInvestment.whole) + "."}
                          <span
                            style={{
                              fontSize: screenSize >= 768 ? ".965rem" : screenSize <= 767 && screenSize >= 576 ? "0.75rem" : ".7rem",
                            }}
                          >
                            {totalInvestment.decimal}
                          </span>
                        </div>
                      ) : (
                        <div
                          style={{
                            fontSize: "1.75rem",
                            fontWeight: "500",
                            marginTop: "0.36rem",
                            letterSpacing: ".03em",
                            color: theme === "light" ? "rgb(0,0,0)" : "rgb(237, 153, 243)",
                          }}
                        >
                          -
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-12 flex-grow-1 p-0'>
                <div className='row w-100 h-100 mx-auto gap-3'>
                  <div className='col-12 col-sm p-0'>
                    <div
                      className='d-flex flex-column align-items-center justify-content-center gap-1 h-100 p-0'
                      style={{
                        lineHeight: "1",
                        // rowGap: '0.45rem',
                        // backgroundColor: theme === "light" ? "rgba(255,255,255,0.7)" : "rgb(100, 100, 100)",
                        backgroundImage: "linear-gradient(120deg, var(--theme-card-primary-bg) 0%, var(--theme-card-secondary-bg) 100%)",
                        border: "1px solid",
                        borderColor: "var(--theme-card-border)",
                        boxShadow: "rgba(99, 99, 99, 0.15) 0px 2px 8px 2px",
                        borderRadius: "20px",
                        minHeight: "100px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "1.05rem",
                          fontWeight: "500",
                          letterSpacing: ".04em",
                          color: theme === "light" ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.75)",
                        }}
                      >
                        {t("portfolio.card.return")}
                      </div>
                      {totalReturn !== null ? (
                        <div
                          dir='ltr'
                          style={{
                            fontSize: "1.75rem",
                            fontWeight: "500",
                            marginTop: "0.36rem",
                            letterSpacing: ".03em",
                            color: theme === "light" ? "rgb(0,0,0)" : "rgb(237, 153, 243)",
                          }}
                        >
                          <span
                            dir='ltr'
                            style={{
                              fontSize: screenSize <= 767 ? "0.7rem" : "1rem",
                              marginRight: isRTL ? ".5rem" : ".25rem",
                              color: theme === "dark" ? "rgb(195, 200, 212)" : "rgb(16 109 221 / 76%)",
                            }}
                          >
                            {t("app.fund.rupee")}
                          </span>
                          {numberWithCommas(totalReturn.whole) + "."}
                          <span
                            style={{
                              fontSize: screenSize >= 768 ? ".965rem" : screenSize <= 767 && screenSize >= 576 ? "0.75rem" : ".7rem",
                            }}
                          >
                            {totalReturn.decimal}
                          </span>
                        </div>
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                  <div className='col-12 col-sm p-0'>
                    <div
                      className='d-flex flex-column align-items-center justify-content-center gap-1 h-100 p-0'
                      style={{
                        lineHeight: "1",
                        // rowGap: '0.45rem',
                        // backgroundColor: theme === "light" ? "rgba(255,255,255,0.7)" : "rgb(100, 100, 100)",
                        backgroundImage: "linear-gradient(120deg, var(--theme-card-primary-bg) 0%, var(--theme-card-secondary-bg) 100%)",
                        border: "1px solid",
                        borderColor: "var(--theme-card-border)",
                        boxShadow: "rgba(99, 99, 99, 0.15) 0px 2px 8px 2px",
                        borderRadius: "20px",
                        minHeight: "100px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "1.05rem",
                          fontWeight: "500",
                          letterSpacing: ".04em",
                          color: theme === "light" ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.75)",
                        }}
                      >
                        {t("portfolio.card.funds")}
                      </div>
                      <div
                        style={{
                          fontSize: "1.75rem",
                          letterSpacing: ".03em",
                          fontWeight: "500",
                          marginTop: "0.36rem",
                          color: theme === "light" ? "rgb(0,0,0)" : "rgb(237, 153, 243)",
                        }}
                      >
                        {portfolioFunds.length !== 0 ? portfolioFunds.length : "-"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-lg-6 col-lg-6 p-0 ps-lg-2 animate-card'>
            <div
              className='p-4 position-relative'
              style={{
                // backgroundColor: theme === "light" ? "rgba(255,255,255,0.7)" : "rgb(100, 100, 100)",
                backgroundImage: "linear-gradient(120deg, var(--theme-card-primary-bg) 0%, var(--theme-card-secondary-bg) 100%)",
                border: "1px solid",
                borderColor: "var(--theme-card-border)",
                borderRadius: "20px",
                boxShadow: "rgba(99, 99, 99, 0.15) 0px 2px 8px 2px",
              }}
            >
              <div
                className='d-flex flex-column flex-sm-row align-items-center justify-content-between flex-nowrap gap-2'
                style={{
                  position: screenSize >= 576 ? 'absolute' : '',
                  top: screenSize >= 576 ? "12px" : "9px",
                  left: screenSize >= 576 ? "18px" : "14px",
                  right: screenSize >= 576 ? "12px" : "9px",
                  zIndex: 100
                }}
              >
                <div
                  className='text-center text-sm-start'
                  style={{
                    fontSize: screenSize >= 576 ? "22px" : "22px",
                    color: theme === "dark" ? "rgba(255,255,255,.7)" : "",
                  }}
                >
                  {viewChartMode === "fund" ? t("app.chart.fund.title.fund") : t("app.chart.fund.title.category")}
                </div>
                <div className='d-flex align-items-center gap-2 flex-nowrap'>
                  <button
                    className={`btn d-flex align-items-center justify-content-center overflow-visible${viewChartMode === "fund" ? " active" : ""}`}
                    onClick={() => setViewChartMode(viewChartMode === "fund" ? "category" : "fund")}
                    style={{
                      borderRadius: "8px",
                      paddingInline: "13px",
                      paddingBlock: "5px",
                      letterSpacing: ".03em",
                      fontSize: screenSize >= 576 ? "13px" : "13px",
                      minWidth: screenSize >= 576 ? "89px" : "89px",
                      ...tabButton(theme),
                    }}
                  >
                    {t("app.chart.fund.button.fund")}
                  </button>
                  <button
                    className={`btn d-flex align-items-center justify-content-center overflow-visible${viewChartMode === "category" ? " active" : ""}`}
                    style={{
                      borderRadius: "8px",
                      paddingInline: "13px",
                      paddingBlock: "5px",
                      letterSpacing: ".03em",
                      fontSize: screenSize >= 576 ? "13px" : "13px",
                      minWidth: screenSize >= 576 ? "89px" : "89px",
                      ...tabButton(theme),
                    }}
                    onClick={() => setViewChartMode(viewChartMode === "fund" ? "category" : "fund")}
                  >
                    {t("app.chart.fund.button.category")}
                  </button>
                </div>
              </div>
              <DonutChart
                data={portfolioFunds}
                showTitle={false}
                view={viewChartMode}
                style={{
                  minHeight: screenSize >= 576 ? "320px" : "350px",
                }}
              />

            </div>
          </div>
        </div>
      </div>
      <div className='col-12 p-0 animate-card'>
        <div
          className='row w-100 mx-auto flex-column'
          style={{
            // backgroundColor: theme === "light" ? "rgba(255,255,255,0.7)" : "rgb(100, 100, 100)",
            backgroundImage: "linear-gradient(120deg, var(--theme-card-primary-bg) 0%, var(--theme-card-secondary-bg) 100%)",
            border: "1px solid",
            borderColor: "var(--theme-card-border)",
            borderRadius: "20px",
            boxShadow: "rgba(99, 99, 99, 0.15) 0px 2px 8px 2px",
            padding: screenSize >= 576 ? ".7rem" : ".6rem",
          }}
        >
          <div className='col-12 p-0'>
            <div
              className=''
              style={{
                display: 'grid',
                gridTemplateColumns: screenSize >= 768 ? '1fr' : (screenSize <= 767 && screenSize >= 576) ? '1fr 1fr' : '1fr',
                gridTemplateRows: 'auto',
                gap: theme === "light" ? ".4rem" : ".7rem",
              }}
            >
              {" "}
              {portfolioFunds.map((data, index) => {
                const hasNavData = data.navData && Object.keys(data.navData).length !== 0 ? true : false;

                const tempNav = parseFloat(data.nav) || 0;
                const tempDataNav = parseFloat(data.navData?.nav || 0);
                const tempUnits = parseFloat(data.fund_unit || 0);
                const tempInvestment = parseFloat(tempNav * tempUnits);
                const tempPortfolio = parseFloat(tempDataNav * tempUnits);
                const tempReturn = parseFloat(tempPortfolio - tempInvestment);

                // const navAtPurchase = format4(data.nav || 0)
                const units = separateNumberParts(format4(data.fund_unit));
                const investment = data.investment ? separateNumberParts(format4(data.investment)) : separateNumberParts(format4(tempInvestment));
                const currentPortfolio = separateNumberParts(format4(tempPortfolio));
                const updatedReturn = data.investment ? (tempPortfolio - data.investment) : tempReturn;
                const fundReturn = separateNumber(updatedReturn, { format: true, digits: 2 });

                return (
                  <div className='' key={index}>
                    <div
                      className='row w-100 mx-auto flex-column flex-md-row align-items-center flex-nowrap'
                      style={{
                        // backgroundColor: theme === "dark" && "rgb(58, 58, 58)",
                        backgroundColor: theme === "dark" && "var(--theme-card-item-bg)",
                        borderRadius: ".8rem",
                        rowGap: "18px",
                        paddingInline: screenSize >= 576 ? ".7rem" : ".65rem",
                        paddingBlock: theme === "light" ? ".7rem" : screenSize >= 576 ? ".8rem" : "18px",
                      }}
                    >
                      <div
                        className='col-auto col-md-2 col-lg-2 col-xl-2 p-0 ps-sm-1 pe-sm-2 text-center text-md-start'
                      >
                        <div
                          className='text-truncate'
                          style={{
                            fontSize: screenSize >= 768 ? "1.5rem" : screenSize >= 576 && screenSize <= 767 ? "1.3rem" : "1.3rem",
                            lineHeight: "1",
                            fontWeight: "500",
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: "3",
                            overflow: "hidden",
                            color: theme === "dark" ? "rgb(218 131 224)" : "var(--theme-light-primary)",
                          }}
                        >
                          {data.fund_code}
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: screenSize >= 768 ? "0.75rem" : screenSize >= 576 && screenSize <= 767 ? ".68rem" : ".68rem",
                            letterSpacing: ".03em",
                            fontWeight: "400",
                            lineHeight: "1",
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: "2",
                            overflow: "hidden",
                            color: theme === "dark" ? "rgba(255,255,255,.53)" : "var(--theme-light-secondary)",
                            marginTop: "2px",
                          }}
                        >
                          {data.fund_name}
                        </div>
                      </div>
                      <div
                        className='col-12'
                        style={{
                          display: screenSize >= 768 ? "none" : "",
                        }}
                      >
                        <hr className='my-0' style={{ color: theme === "dark" ? "rgb(255,255,255)" : "rgb(0,0,0)" }} />
                      </div>
                      <div className='col-12 col-md col-lg col-xl-8 p-0'>
                        <div
                          className='row w-100 mx-auto'
                          style={{
                            rowGap: "18px",
                            lineHeight: "1",
                          }}
                        >
                          <div
                            className='col-6 col-md-3 p-0'
                            style={{
                              width: screenSize >= 768 ? "" : "44%",
                              textAlign: screenSize >= 768 ? "right" : "center",
                            }}
                          >
                            <div
                              style={{
                                minWidth: screenSize >= 576 ? "76px" : "58px",
                                display: "inline-block",
                                textAlign: screenSize >= 576 ? "right" : "center",
                              }}
                            >
                              <div
                                className=''
                                style={{
                                  fontSize: screenSize >= 768 ? "0.74rem" : screenSize >= 576 && screenSize <= 767 ? "0.65rem" : ".65rem",
                                  fontWeight: "400",
                                  letterSpacing: ".04em",
                                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary)" : "var(--theme-light-tertiary)"},0.5)`,
                                }}
                              >
                                {t("portfolio.fund.units")}
                              </div>
                              <div
                                className=''
                                style={{
                                  fontSize: screenSize >= 768 ? "1.14rem" : screenSize <= 767 && screenSize >= 576 ? "1.05rem" : "0.975rem",
                                  fontWeight: "400",
                                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary), 0.8" : "var(--theme-light-tertiary), 0.75"})`,
                                  marginTop: screenSize <= 767 ? "3px" : "2px",
                                }}
                              >
                                {numberWithCommas(units.whole)}
                                {units.hasDecimal ? "." : ""}
                                {units.hasDecimal ? (
                                  <span
                                    style={{
                                      fontSize: screenSize >= 768 ? ".965rem" : screenSize <= 767 && screenSize >= 576 ? "0.75rem" : ".7rem",
                                    }}
                                  >
                                    {units.decimal}
                                  </span>
                                ) : (
                                  ""
                                )}
                              </div>
                            </div>
                          </div>

                          <div
                            className='col-6 col-md-3 p-0'
                            style={{
                              width: screenSize >= 768 ? "" : "56%",
                              textAlign: screenSize >= 768 ? "right" : "center",
                            }}
                          >
                            <div
                              className='d-inline-block d-md-block'
                              style={{
                                minWidth: "58px",
                                textAlign: screenSize >= 576 ? "right" : "center",
                              }}
                            >
                              <div
                                className=''
                                style={{
                                  fontSize: screenSize >= 768 ? "0.74rem" : screenSize >= 576 && screenSize <= 767 ? "0.65rem" : ".65rem",
                                  fontWeight: "400",
                                  letterSpacing: ".04em",
                                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary)" : "var(--theme-light-tertiary)"},0.5)`,
                                }}
                              >
                                {t("portfolio.fund.invest")}
                              </div>
                              <div
                                className=''
                                dir='ltr'
                                style={{
                                  fontSize: screenSize >= 768 ? "1.14rem" : screenSize <= 767 && screenSize >= 576 ? "1.05rem" : "0.975rem",
                                  fontWeight: "400",
                                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary), 0.8" : "var(--theme-light-tertiary), 0.75"})`,
                                  marginTop: screenSize <= 767 ? "3px" : "2px",
                                }}
                              >
                                <span
                                  dir='ltr'
                                  style={{
                                    fontSize: screenSize <= 767 ? "0.7rem" : "0.8rem",
                                    marginRight: ".25rem",
                                    color: theme === "dark" ? "rgba(212, 129, 217, .8)" : "rgb(16 109 221 / 76%)",
                                  }}
                                >
                                  {t("app.fund.rupee")}
                                </span>
                                {numberWithCommas(investment.whole) + "."}

                                <span
                                  style={{
                                    fontSize: screenSize >= 768 ? ".965rem" : screenSize <= 767 && screenSize >= 576 ? "0.75rem" : ".7rem",
                                    color: theme === "dark" ? "rgba(180, 180, 180, 1)" : "rgba(93, 93, 93, 1)",
                                  }}
                                >
                                  {investment.decimal}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div
                            className='col-6 col-md-3 p-0'
                            style={{
                              width: screenSize >= 768 ? "" : "44%",
                              textAlign: screenSize >= 768 ? "right" : "center",
                            }}
                          >
                            <div
                              style={{
                                minWidth: screenSize >= 576 ? "76px" : "58px",
                                display: "inline-block",
                                textAlign: screenSize >= 576 ? "right" : "center",
                              }}
                            >
                              <div
                                className=''
                                style={{
                                  fontSize: screenSize >= 768 ? "0.74rem" : screenSize >= 576 && screenSize <= 767 ? "0.65rem" : ".65rem",
                                  fontWeight: "400",
                                  letterSpacing: ".04em",
                                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary)" : "var(--theme-light-tertiary)"},0.5)`,
                                }}
                              >
                                {t("portfolio.fund.size")}
                              </div>
                              <div
                                className=''
                                dir='ltr'
                                style={{
                                  fontSize: screenSize >= 768 ? "1.14rem" : screenSize <= 767 && screenSize >= 576 ? "1.05rem" : "0.975rem",
                                  fontWeight: "400",
                                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary), 0.8" : "var(--theme-light-tertiary), 0.75"})`,
                                  marginTop: screenSize <= 767 ? "3px" : "2px",
                                }}
                              >
                                <span
                                  dir='ltr'
                                  style={{
                                    fontSize: screenSize <= 767 ? "0.7rem" : "0.8rem",
                                    marginRight: ".25rem",
                                    color: theme === "dark" ? "rgba(212, 129, 217, .8)" : "rgb(16 109 221 / 76%)",
                                  }}
                                >
                                  {t("app.fund.rupee")}
                                </span>
                                {numberWithCommas(currentPortfolio.whole) + "."}
                                <span
                                  style={{
                                    fontSize: screenSize >= 768 ? ".965rem" : screenSize <= 767 && screenSize >= 576 ? "0.75rem" : ".7rem",
                                    color: theme === "dark" ? "rgba(180, 180, 180, 1)" : "rgba(93, 93, 93, 1)",
                                  }}
                                >
                                  {currentPortfolio.decimal}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div
                            className='col-6 col-md-3 p-0'
                            style={{
                              width: screenSize >= 768 ? "" : "56%",
                              textAlign: screenSize >= 768 ? "right" : "center",
                            }}
                          >
                            <div
                              className='d-inline-block d-md-block'
                              style={{
                                minWidth: "58px",
                                textAlign: screenSize >= 576 ? "right" : "center",
                              }}
                            >
                              <div
                                className=''
                                style={{
                                  fontSize: screenSize >= 768 ? "0.74rem" : screenSize >= 576 && screenSize <= 767 ? "0.65rem" : ".65rem",
                                  fontWeight: "400",
                                  letterSpacing: ".04em",
                                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary)" : "var(--theme-light-tertiary)"},0.5)`,
                                }}
                              >
                                {t("portfolio.fund.return")}
                              </div>
                              <div
                                className=''
                                dir='ltr'
                                style={{
                                  fontSize: screenSize >= 768 ? "1.14rem" : screenSize <= 767 && screenSize >= 576 ? "1.05rem" : "0.975rem",
                                  fontWeight: "400",
                                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary), 0.8" : "var(--theme-light-tertiary), 0.75"})`,
                                  marginTop: screenSize <= 767 ? "3px" : "2px",
                                }}
                              >
                                <span
                                  dir='ltr'
                                  style={{
                                    fontSize: screenSize <= 767 ? "0.7rem" : "0.8rem",
                                    marginRight: ".25rem",
                                    color: theme === "dark" ? "rgba(212, 129, 217, .8)" : "rgb(16 109 221 / 76%)",
                                  }}
                                >
                                  {t("app.fund.rupee")}
                                </span>
                                {numberWithCommas(fundReturn.whole) + "."}

                                <span
                                  style={{
                                    fontSize: screenSize >= 768 ? ".965rem" : screenSize <= 767 && screenSize >= 576 ? "0.75rem" : ".7rem",
                                    color: theme === "dark" ? "rgba(180, 180, 180, 1)" : "rgba(93, 93, 93, 1)",
                                  }}
                                >
                                  {fundReturn.decimal}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* {screenSize <= 575 ? (
										<div
											className='col d-flex align-items-center justify-content-start p-0 ps-1 pe-sm-2'
											style={
											{
												width: screenSize >= 576 ? "" : "44%"
												// width: screenSize >= 992 && screenSize <= 1199 ? "12%" : (screenSize >= 768 && screenSize <= 991) ? "12%" : (screenSize >= 576 && screenSize <= 767) ? "12%" : (screenSize <= 575) ? "18%" : "",
											}
											}
										>
											<button
											className='btn btn-primary'
											type='button'
											onClick={() => toggleRedeemModal(data)}
											style={{
												// backgroundColor: 'var(--theme-light-primary)',
												border: "none",
												borderRadius: "6px",
												color: "rgba(255,255,255,.85)",
												fontSize: screenSize >= 768 ? ".85rem" : ".7rem",
												fontWeight: "400",
												padding: screenSize >= 768 ? ".3rem .8rem .35rem" : ".3rem .8rem .35rem",
											}}
											>
											Redeem
											</button>
										</div>
										) : (
										""
										)} */}
                        </div>
                      </div>
                      <div
                        className='col-12'
                        style={{
                          display: screenSize >= 768 ? "none" : "",
                        }}
                      >
                        <hr className='my-0' style={{ color: theme === "dark" ? "rgb(255,255,255)" : "rgb(0,0,0)" }} />
                      </div>
                      {/* {screenSize >= 576 ? ( */}
                      <div
                        className='col-auto col-md-3 col-lg-3 col-xl-2 d-flex flex-md-column align-items-end justify-content-center justify-content-md-end p-0 pe-sm-2 gap-2 flex-nowrap'
                        style={{
                          width:
                            screenSize >= 992 && screenSize <= 1199
                              ? "15%"
                              : screenSize >= 768 && screenSize <= 991
                                ? "15%"
                                : screenSize >= 576 && screenSize <= 767
                                  ? "18%"
                                  : screenSize <= 575
                                    ? "auto"
                                    : "",
                        }}
                      >
                        <button
                          className='btn btn-primary'
                          type='button'
                          onClick={() => toggleRedeemModal(data)}
                          style={{
                            // backgroundColor: 'var(--theme-light-primary)',
                            border: "none",
                            borderRadius: "6px",
                            minWidth: "75px",
                            color: "rgba(255,255,255,.85)",
                            fontSize: screenSize >= 768 ? ".85rem" : ".7rem",
                            fontWeight: "400",
                            padding: screenSize >= 768 ? ".3rem .8rem .35rem" : ".3rem .8rem .35rem",
                          }}
                        >
                          {t("portfolio.fund.redeem")}
                        </button>
                        <button
                          className='btn btn-primary'
                          type='button'
                          onClick={() => toggleConversionModal(data)}
                          style={{
                            // backgroundColor: 'var(--theme-light-primary)',
                            border: "none",
                            borderRadius: "6px",
                            minWidth: "75px",
                            color: "rgba(255,255,255,.85)",
                            fontSize: screenSize >= 768 ? ".85rem" : ".7rem",
                            fontWeight: "400",
                            padding: screenSize >= 768 ? ".3rem .8rem .35rem" : ".3rem .8rem .35rem",
                          }}
                        >
                          {t("portfolio.fund.convert")}
                        </button>
                      </div>
                      {/* ) : (
									""
									)} */}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* -- CONVERSION FUND - MODAL -- */}
      <div className='modal fade' id='conversionModal' tabIndex='-1' aria-labelledby='conversionModal' aria-hidden='true' ref={conversionModalRef}>
        <div
          className='modal-dialog modal-dialog-centered'
          style={{
            ["--bs-modal-width"]: "600px",
            margin: screenSize >= 616 ? "auto" : ".5rem",
          }}
        >
          <form
            className='modal-content'
            style={{
              backgroundColor: theme === "light" ? "rgb(243, 243, 243)" : "rgb(50, 50, 50)",
              border: "none",
            }}
          >
            <div
              className='modal-header align-items-center justify-content-between border-0 position-relative'
              style={{
                backgroundColor: theme === "light" ? "rgb(173, 115, 176)" : "rgb(145, 78, 148)",
                minHeight: "67px",
                padding: screenSize >= 576 ? "1rem .8rem" : ".75rem .65rem",
              }}
            >
              <div
                className='ps-2'
                style={{
                  color: "rgba(255,255,255,.7)",
                  fontSize: screenSize >= 576 ? "1rem" : ".82rem",
                }}
              >
                {/* {t("modals.common.step")} 1<span style={{ margin: "0 .125rem" }}>/</span>2 */}
              </div>

              <h1
                className='modal-title position-absolute start-50 translate-middle-x text-center text-nowrap'
                id='goalModal'
                style={{ color: "rgb(224, 232, 240)", fontSize: screenSize >= 576 ? "1.45rem" : "1.25rem", lineHeight: screenSize >= 576 ? "" : "1.1" }}
              >
                {t("modals.convert.info.title")}
              </h1>
              <button
                type='button'
                className='btn-close p-0 d-flex align-items-center justify-content-center'
                aria-label='Close'
                style={{
                  marginLeft: "0",
                  marginRight: ".15rem",
                  color: "rgb(255,255,255)",
                  background: "none",
                  height: screenSize >= 576 ? "32px" : "28px",
                  width: screenSize >= 576 ? "32px" : "28px",
                  ["--bs-btn-close-opacity"]: "0.65",
                  ["--bs-btn-close-hover-opacity"]: "0.85",
                }}
                onClick={() => {
                  if (conversionModalRef.current) {
                    conversionModalInstanceRef.current?.toggle();
                  }
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  height={screenSize >= 576 ? 18 : 16}
                  width={screenSize >= 576 ? 18 : 16}
                  stroke='currentColor'
                  fill='currentColor'
                  viewBox='0 0 16 16'
                >
                  <path d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414'></path>
                </svg>
              </button>
            </div>

            <hr className='m-0' />

            <div className='modal-body p-0'>
              <div className='row w-100 mx-auto gap-3 p-3 pb-4'>
                <div
                  className='col-12 p-0 form-label mb-2 d-block text-center'
                  style={{
                    fontSize: "1.25rem",
                    color: theme === "light" ? "rgba(0,0,0,0.6)" : "var(--theme-light-secondary)",
                  }}
                >
                  <div className='row w-100 mx-auto align-items-center flex-nowrap'>
                    <div className='col p-0'>
                      <hr className='mx-1 mt-1 mb-0' style={{ color: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)" }} />
                    </div>
                    <div className='col-auto p-0 px-1'>{t("modals.convert.info.from")}</div>
                    <div className='col p-0'>
                      <hr className='mx-1 mt-1 mb-0' style={{ color: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)" }} />
                    </div>
                  </div>
                </div>
                <div className='col-12 p-0'>
                  <div className='row w-100 mx-auto py-sm-2 justify-content-center align-items-center flex-nowrap column-gap-5 mb-3'>
                    <div className='col-auto p-0 text-center'>
                      <div
                        className=''
                        style={{
                          fontSize: "13px",
                          fontWeight: "400",
                          letterSpacing: ".03em",
                          color: "rgb(170, 170, 170)",
                        }}
                      >
                        {t("modals.redeem.info.fund")}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: "23px",
                          lineHeight: "1",
                          fontWeight: "500",
                          color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                          marginTop: "5px",
                        }}
                      >
                        {activeRedeemFund?.fund_code}
                      </div>
                      <div
                        className='text-truncate'
                        data-tooltip-id={"tt-simple"}
                        data-tooltip-content={activeRedeemFund?.fund_name}
                        dir='ltr'
                        style={{
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: "400",
                          lineHeight: "1",
                          color: theme === "dark" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                          marginTop: "3px",
                          maxWidth: "110px",
                        }}
                      >
                        {activeRedeemFund?.fund_name}
                      </div>
                      <Tooltip
                        // anchorSelect={"asd"}
                        id={"tt-simple"}
                        place='bottom'
                        // offset={12}
                        // delayShow={120}
                        className='my-rt'
                        classNameArrow='my-rt-arrow'
                        border='1px solid rgba(255,255,255,0.12)'
                        arrowSize={8}
                        opacity={0.95}
                      />
                    </div>
                    <div className='col-auto p-0 text-center'>
                      <div
                        className=''
                        style={{
                          fontSize: "13px",
                          fontWeight: "400",
                          letterSpacing: ".03em",
                          color: "rgb(170, 170, 170)",
                        }}
                      >
                        {t("app.fund.price")}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: "23px",
                          lineHeight: "1",
                          fontWeight: "500",
                          color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                          marginTop: "5px",
                        }}
                      >
                        {numberWithCommas(redemptionPrice?.whole)}
                        {redemptionPrice.hasDecimal ? "." : ""}
                        {redemptionPrice.hasDecimal ? <span style={{ fontSize: "16px" }}>{redemptionPrice.decimal}</span> : ""}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: "12px",
                          fontWeight: "400",
                          lineHeight: "1",
                          color: theme === "dark" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                          marginTop: "3px",
                          minHeight: "13.6px",
                        }}
                      >
                        {t("app.fund.rs")}
                      </div>
                    </div>
                    <div className='col-auto p-0 text-center'>
                      <div
                        className=''
                        style={{
                          fontSize: "13px",
                          fontWeight: "400",
                          letterSpacing: ".03em",
                          color: "rgb(170, 170, 170)",
                        }}
                      >
                        {t("modals.convert.form.units")}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: "23px",
                          lineHeight: "1",
                          fontWeight: "500",
                          color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                          marginTop: "5px",
                        }}
                      >
                        {numberWithCommas(unitHoldings.whole)}
                        {unitHoldings.hasDecimal ? "." : ""}
                        {unitHoldings.hasDecimal ? <span style={{ fontSize: "15px" }}>{unitHoldings.decimal}</span> : ""}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: "12px",
                          fontWeight: "400",
                          lineHeight: "1",
                          color: theme === "dark" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                          marginTop: "3px",
                          minHeight: "13.6px",
                        }}
                      >
                        {t("app.fund.units")}
                      </div>
                    </div>
                    <div className='col-auto p-0 text-center'>
                      <div
                        className=''
                        style={{
                          fontSize: "13px",
                          fontWeight: "400",
                          letterSpacing: ".03em",
                          color: "rgb(170, 170, 170)",
                        }}
                      >
                        {t("modals.redeem.payment.amount")}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: "23px",
                          lineHeight: "1",
                          fontWeight: "500",
                          color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                          marginTop: "5px",
                        }}
                      >
                        {numberWithCommas(amount.whole)}
                        {amount.hasDecimal ? "." : ""}
                        {amount.hasDecimal ? <span style={{ fontSize: "15px" }}>{amount.decimal}</span> : ""}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: "12px",
                          fontWeight: "400",
                          lineHeight: "1",
                          color: theme === "dark" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                          marginTop: "3px",
                          minHeight: "13.6px",
                        }}
                      >
                        {t("app.fund.rs")}
                      </div>
                    </div>
                  </div>
                </div>
                <div className='col p-0 pe-1 mb-2'>
                  <div
                    className='d-flex flex-column align-items-center justify-content-center flex-nowrap mx-auto'
                    style={{
                      minHeight: "180px",
                      maxWidth: "380px",
                    }}
                  >
                    <div className='w-100 mx-auto mb-3'>
                      <div
                        className='d-flex align-items-center justify-content-center flex-nowrap text-center mb-2'
                        style={{
                          fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                          color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                          fontWeight: "400",
                          lineHeight: "1",
                        }}
                      >
                        {t("modals.convert.form.amount")}

                        <div className='ms-3 p-0'>
                          <input
                            className='form-check-input m-0'
                            type='checkbox'
                            checked={
                              conversionData.total_amount
                                ? parseFloat(conversionData.total_amount) ==
                                  Number(parseFloat(activeRedeemFund?.navData?.redemption_price || 0) * parseFloat(activeRedeemFund?.fund_unit || 0)).toFixed(4)
                                : false
                            }
                            onChange={() => {
                              const totalAmount = Number(parseFloat(activeRedeemFund?.navData?.redemption_price || 0) * parseFloat(activeRedeemFund?.fund_unit || 0)).toFixed(4);

                              if (parseFloat(conversionData.total_amount) == totalAmount) {
                                handleConversionChange({ target: { name: "total_amount", value: "" } });
                              } else {
                                handleConversionChange({ target: { name: "total_amount", value: format4(totalAmount) } });
                              }
                              // setConversionData(prev => ({ ...prev, allUnits: !prev.allUnits }))
                            }}
                            value='all'
                            id='checkDefault'
                            style={{
                              height: "13px",
                              width: "13px",
                            }}
                          />
                        </div>
                        <div className='ms-1'>All</div>
                      </div>

                      {/* <div className='w-100 d-flex align-items-center flex-nowrap overflow-auto gap-2 px-3 mb-2' style={{
                        scrollbarWidth: 'none'
                      }}>
                        {["50000", "100000", "500000", "1000000"].map((num, idx) => {
                          return (
                            <button
                              className={`btn${parseFloat(conversionData.total_amount) === parseFloat(num) ? " active" : ""}`}
                              value={num}
                              key={idx}
                              name='total_amount'
                              onClick={(e) => handleConversionChange(e)}
                              type='button'
                              style={{
                                fontSize: "13px",
                                paddingBlock: "3px",
                                fontWeight: "300",
                                lineHeight: "1.25",
                                flex: "1",
                                borderRadius: "999px",
                                ...tabButton(theme),
                              }}
                            >
                              {numberWithCommas(num)}
                            </button>
                          );
                        })}
                      </div> */}

                      <input
                        type='text'
                        className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""} rounded-pill text-center`}
                        name='total_amount'
                        value={conversionData.total_amount && numberWithCommas(conversionData.total_amount)}
                        style={{
                          ...themeBasedPurchaseFormControl(theme),
                          marginTop: ".4rem",
                          fontSize: screenSize >= 576 ? "1.1rem" : "1rem",
                        }}
                        onChange={handleConversionChange}
                      />

                      <div
                        className='text-center mt-2'
                        style={{
                          fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                          color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                          fontWeight: "400",
                          lineHeight: "1",
                          minHeight: "14px",
                        }}
                      >
                        {conversionData.total_amount ? amountToWords(conversionData.total_amount) : ""}
                      </div>
                    </div>
                    <div className='row w-100 mx-auto position-relative'>
                      {/* <div className='col p-0 pe-1'> <div
                          className='text-center'
                          style={{
                            fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                            color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                            fontWeight: "400",
                            lineHeight: "1",
                          }}
                        >
                          {t("app.fund.price")}
                        </div>
                        <input
                          type='text'
                          disabled
                          className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""} rounded-pill text-center`}
                          value={activeRedeemFund?.navData?.redemption_price ? format4(activeRedeemFund?.navData?.redemption_price) : "0.0000"}
                          style={{
                            ...themeBasedPurchaseFormControl(theme),
                            marginTop: ".4rem",
                            fontSize: screenSize >= 576 ? "1.1rem" : "1rem",
                          }}
                        />
                      </div>
                      <div className='col-auto p-0'>
                        <div className='d-flex flex-column justify-content-end align-items-center h-100 pb-2'>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width={26}
                            height={26}
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='var(--theme-light-primary)'
                            strokeWidth={1.5}
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='icon icon-tabler icons-tabler-outline icon-tabler-x'
                          >
                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                            <path d='M18 6l-12 12' />
                            <path d='M6 6l12 12' />
                          </svg>
                        </div>
                      </div> */}
                      <div className='col p-0 ps-1'>
                        <div
                          className='text-center d-flex align-items-center justify-content-center flex-nowrap'
                          style={{
                            fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                            color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                            fontWeight: "400",
                            lineHeight: "1",
                          }}
                        >
                          {t("modals.convert.form.out_units")}

                          <div className='ms-3 p-0'>
                            <input
                              className='form-check-input m-0'
                              type='checkbox'
                              checked={conversionData.fund_units ? parseFloat(conversionData.fund_units) == activeRedeemFund?.fund_unit : false}
                              onChange={() => {
                                if (parseFloat(conversionData.fund_units) == activeRedeemFund?.fund_unit) {
                                  handleConversionChange({ target: { name: "fund_units", value: "" } });
                                } else {
                                  handleConversionChange({ target: { name: "fund_units", value: String(activeRedeemFund?.fund_unit || "") } });
                                }
                                // setConversionData(prev => ({ ...prev, allUnits: !prev.allUnits }))
                              }}
                              value='all'
                              id='checkDefault'
                              style={{
                                height: "13px",
                                width: "13px",
                              }}
                            />
                          </div>
                          <div className='ms-1'>All</div>
                        </div>
                        <input
                          type='text'
                          name='fund_units'
                          value={conversionData.fund_units ? numberWithCommas(conversionData.fund_units) : ""}
                          className={`form-control-with-error ${validationError ? "" : `goal-form-control${theme === "dark" ? "-dark" : ""}`} rounded-pill text-center`}
                          style={
                            validationError
                              ? {
                                  ...themeBasedPurchaseFormControl(theme),
                                  boxShadow: "0 0 2px .15rem rgba(192, 19, 31, 0.25)",
                                  borderColor: "rgb(192, 19, 31)",
                                  marginTop: ".4rem",
                                  fontSize: screenSize >= 576 ? "1.1rem" : "1rem",
                                }
                              : {
                                  ...themeBasedPurchaseFormControl(theme),
                                  // boxShadow: validationError ? '0 0 2px .15rem rgba(192, 19, 31, 0.25)' : '',
                                  marginTop: ".4rem",
                                  fontSize: screenSize >= 576 ? "1.1rem" : "1rem",
                                }
                          }
                          onChange={handleConversionChange}
                        />
                      </div>
                      {/* <div className="col-auto p-0" style={{
                        position: 'absolute',
                        top: '0',
                        right: '-60px'
                      }}>
                        <div
                          className='text-center'
                          style={{
                            fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                            color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                            fontWeight: "400",
                            lineHeight: "1",
                          }}
                        >
                          {"All Units"}
                        </div>
                        <div className="d-flex align-items-center justify-content-center" style={{
                          height: '40px',
                          maxHeight: '100%'
                        }}>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            checked={(conversionData.fund_units) ? parseFloat(conversionData.fund_units) == activeRedeemFund?.fund_unit : false}
                            onChange={() => {

                              if (parseFloat(conversionData.fund_units) == activeRedeemFund?.fund_unit) {
                                handleConversionChange({ target: { name: "fund_units", value: "" }})
                              } else {
                                handleConversionChange({ target: { name: "fund_units", value: String(activeRedeemFund?.fund_unit || "") }})
                              }
                              // setConversionData(prev => ({ ...prev, allUnits: !prev.allUnits }))
                            }}
                            value='all'
                            id='checkDefault'
                          />
                        </div>
                      </div> */}
                    </div>
                  </div>
                </div>
                <div className='col-12 p-0 mb-4'>
                  <div
                    className='mx-auto'
                    style={{
                      maxWidth: "540px",
                    }}
                  >
                    <div
                      className='form-label mb-3 d-block text-center'
                      style={{
                        fontSize: "1.25rem",
                        color: theme === "light" ? "rgba(0,0,0,0.6)" : "var(--theme-light-secondary)",
                      }}
                    >
                      <div className='row w-100 mx-auto align-items-center flex-nowrap'>
                        <div className='col p-0'>
                          <hr className='mx-1 mt-1 mb-0' style={{ color: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)" }} />
                        </div>
                        <div className='col-auto p-0 px-1'>{t("modals.convert.info.select")}</div>
                        <div className='col p-0'>
                          <hr className='mx-1 mt-1 mb-0' style={{ color: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)" }} />
                        </div>
                      </div>
                    </div>

                    <FundSelector
                      ref={conversionModalRef}
                      title={t("modals.convert.info.select")}
                      showLabel={false}
                      data={conversionData}
                      updateData={setConversionData}
                      filterItems={fundCategories}
                      items={conversionSelectFunds}
                      onItemSelect={(fundItem) => {
                        setConversionData((prev) => {
                          if (prev.fund_units !== "") {
                            const calculatedUnits = format4(parseFloat(prev.total_amount) / parseFloat(fundItem?.navData?.offer_price));

                            return { ...prev, fund: fundItem, calculated_units: calculatedUnits };
                          }
                          return { ...prev, fund: fundItem };
                        });
                      }}
                    />
                  </div>
                </div>

                <div className='col-12 p-0'>
                  <div
                    className='mx-auto'
                    style={{
                      maxWidth: "380px",
                    }}
                  >
                    <div className='row w-100 mx-auto'>
                      <div className='col p-0 pe-1'>
                        <div
                          className='text-center'
                          style={{
                            fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                            color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                            fontWeight: "400",
                            lineHeight: "1",
                          }}
                        >
                          {t("app.fund.price")}
                        </div>
                        <input
                          type='text'
                          disabled
                          className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""} rounded-pill text-center`}
                          value={conversionData.fund ? format4(conversionData.fund?.navData?.offer_price) : "0.0000"}
                          style={{
                            ...themeBasedPurchaseFormControl(theme),
                            marginTop: ".4rem",
                            fontSize: screenSize >= 576 ? "1.1rem" : "1rem",
                          }}
                        />
                      </div>
                      <div className='col-auto p-0'>
                        <div className='d-flex flex-column justify-content-end align-items-center h-100 pb-2'>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width={26}
                            height={26}
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='var(--theme-light-primary)'
                            strokeWidth={1.5}
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='icon icon-tabler icons-tabler-outline icon-tabler-x'
                          >
                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                            <path d='M18 6l-12 12' />
                            <path d='M6 6l12 12' />
                          </svg>
                        </div>
                      </div>
                      <div className='col p-0 ps-1'>
                        <div
                          className='text-center'
                          style={{
                            fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                            color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                            fontWeight: "400",
                            lineHeight: "1",
                          }}
                        >
                          {t("modals.convert.form.in_units")}
                        </div>
                        <input
                          type='text'
                          name='fund_units'
                          disabled
                          value={conversionData.calculated_units ? numberWithCommas(conversionData.calculated_units) : ""}
                          className={`form-control-with-error ${validationError ? "" : `goal-form-control${theme === "dark" ? "-dark" : ""}`} rounded-pill text-center`}
                          style={
                            validationError
                              ? {
                                  ...themeBasedPurchaseFormControl(theme),
                                  boxShadow: "0 0 2px .15rem rgba(192, 19, 31, 0.25)",
                                  borderColor: "rgb(192, 19, 31)",
                                  marginTop: ".4rem",
                                  fontSize: screenSize >= 576 ? "1.1rem" : "1rem",
                                }
                              : {
                                  ...themeBasedPurchaseFormControl(theme),
                                  // boxShadow: validationError ? '0 0 2px .15rem rgba(192, 19, 31, 0.25)' : '',
                                  marginTop: ".4rem",
                                  fontSize: screenSize >= 576 ? "1.1rem" : "1rem",
                                }
                          }
                          // onChange={handleRedeemFundChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='modal-footer border-0 flex-nowrap justify-content-between'>
              <div
                className=''
                style={{
                  maxWidth: screenSize >= 576 ? "calc(100% - 300px)" : "65%",
                  fontSize: screenSize >= 576 ? ".775rem" : ".75rem",
                  lineHeight: "1.2",
                  color: theme === "light" ? "rgba(0,0,0,.6)" : "rgba(255,255,255,.5)",
                }}
              >
                {t("modals.redeem.info.caption")}
              </div>
              <button
                type='button'
                disabled={loading.convertUnits}
                className='btn btn-primary border-0 px-4 pb-2 d-flex align-items-center justify-content-center'
                onClick={() => {
                  (async () => {
                    const args = {
                      email: user.data.email,
                      folio_no: user.data.folio_no || user.data.main_folio_no,
                      fund_code: activeRedeemFund?.fund_code,
                      units_requested: String(conversionData.fund_units),
                      gross_amount_limit: String(conversionData.total_amount),
                      nav_at_request: activeRedeemFund?.navData?.redemption_price || 0,
                      convert_fund_code: conversionData.fund?.symbol_code,
                      convert_units_requested: conversionData.calculated_units,
                      convert_nav_at_request: conversionData.fund?.navData?.offer_price || 0,
                      request_type: "conversionofunits",
                      // payment_method: fundPaymentData.payment_method,
                      investor_bank_name: unitholder.bank_name || "",
                      investor_branch_name: unitholder.branch_name || "",
                      investor_bank_iban: unitholder.account_no || "",
                      investor_comment: unitholder.comment || "",
                      investor_account_title: unitholder.account_title || "",
                    };

                    if (parseFloat(conversionData.fund_units) > parseFloat(activeRedeemFund?.fund_unit)) {
                      toast((t) => customToast("Conversion units cannot exceed your available units.", "error", t), {
                        duration: 3000,
                        removeDelay: 1000,
                        style: {
                          padding: "0.75rem",
                          margin: "0",
                          width: "300px",
                          maxWidth: "100%",
                          // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
                          // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
                          color: "rgb(33,37,41)",
                          backgroundColor: "rgb(221, 204, 225)",
                        },
                      });
                      return;
                    }

                    try {
                      setLoading((prev) => ({ ...prev, convertUnits: true }));

                      const response = await addNewInvestor(args);

                      if (!response.error) {
                        toast((t) => customToast(response.message, "success", t), {
                          duration: 3000,
                          removeDelay: 1000,
                          style: {
                            padding: "0.75rem",
                            margin: "0",
                            width: "300px",
                            maxWidth: "100%",
                            // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
                            // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
                            color: "rgb(33,37,41)",
                            backgroundColor: "rgb(221, 204, 225)",
                          },
                        });

                        if (conversionModalRef.current) {
                          conversionModalInstanceRef.current.hide();
                        }
                      } else {
                        toast((t) => customToast(response.message, "error", t), {
                          duration: 3000,
                          removeDelay: 1000,
                          style: {
                            padding: "0.75rem",
                            margin: "0",
                            width: "300px",
                            maxWidth: "100%",
                            // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
                            // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
                            color: "rgb(33,37,41)",
                            backgroundColor: "rgb(221, 204, 225)",
                          },
                        });
                      }
                    } catch (e) {
                      // ---
                    } finally {
                      setLoading((prev) => ({ ...prev, convertUnits: false }));
                    }

                    // const res = await addNewInvestor(args);
                  })();
                }}
                style={{
                  minHeight: "38px",
                  minWidth: "80px",
                  fontSize: screenSize >= 576 ? "1rem" : ".9rem",
                }}
              >
                {loading.convertUnits ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm'
                      aria-hidden='true'
                      style={{
                        fontSize: "0.85rem",
                        height: screenSize >= 576 ? "15px" : "14px",
                        width: screenSize >= 576 ? "15px" : "14px",
                      }}
                    ></span>
                    {/* <span role="status">Loading...</span> */}
                  </>
                ) : (
                  t("modals.common.confirm")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* -- REDEEM FUND - MODAL -- */}
      <div className='modal fade' id='redeemModal' tabIndex='-1' aria-labelledby='fundModal' aria-hidden='true' ref={redeemModalRef}>
        <div
          className='modal-dialog modal-dialog-centered'
          style={{
            ["--bs-modal-width"]: "600px",
            margin: screenSize >= 616 ? "auto" : ".5rem",
          }}
        >
          <form
            className='modal-content'
            onSubmit={handleRedeemFund}
            style={{
              backgroundColor: theme === "light" ? "rgb(243, 243, 243)" : "rgb(50, 50, 50)",
              border: "none",
            }}
          >
            <div
              className='modal-header align-items-center justify-content-between border-0 position-relative'
              style={{
                backgroundColor: theme === "light" ? "rgb(173, 115, 176)" : "rgb(145, 78, 148)",
                minHeight: "67px",
                padding: screenSize >= 576 ? "1rem .8rem" : ".75rem .65rem",
              }}
            >
              <div
                className='ps-2'
                style={{
                  color: "rgba(255,255,255,.7)",
                  fontSize: screenSize >= 576 ? "1rem" : ".82rem",
                }}
              >
                {t("modals.common.step")} 1<span style={{ margin: "0 .125rem" }}>/</span>2
              </div>

              <h1
                className='modal-title position-absolute start-50 translate-middle-x text-center text-nowrap'
                id='goalModal'
                style={{ color: "rgb(224, 232, 240)", fontSize: screenSize >= 576 ? "1.45rem" : "1.25rem", lineHeight: screenSize >= 576 ? "" : "1.1" }}
              >
                {t("modals.redeem.info.title")}
              </h1>
              <button
                type='button'
                className='btn-close p-0 d-flex align-items-center justify-content-center'
                aria-label='Close'
                style={{
                  marginLeft: "0",
                  marginRight: ".15rem",
                  color: "rgb(255,255,255)",
                  background: "none",
                  height: screenSize >= 576 ? "32px" : "28px",
                  width: screenSize >= 576 ? "32px" : "28px",
                  ["--bs-btn-close-opacity"]: "0.65",
                  ["--bs-btn-close-hover-opacity"]: "0.85",
                }}
                onClick={() => toggleRedeemModal()}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  height={screenSize >= 576 ? 18 : 16}
                  width={screenSize >= 576 ? 18 : 16}
                  stroke='currentColor'
                  fill='currentColor'
                  viewBox='0 0 16 16'
                >
                  <path d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414'></path>
                </svg>
              </button>
            </div>

            <hr className='m-0' />

            <div className='modal-body p-0'>
              <div className='row w-100 mx-auto gap-3 p-3 pb-2'>
                <div className='col-12 p-0'>
                  <div className='row w-100 mx-auto py-sm-2 justify-content-center align-items-center flex-nowrap column-gap-5'>
                    <div
                      className='col-auto p-0 text-center'
                      style={{
                        minWidth: "140px",
                      }}
                    >
                      <div
                        className=''
                        style={{
                          fontSize: ".85rem",
                          fontWeight: "400",
                          letterSpacing: ".03em",
                          color: "rgb(170, 170, 170)",
                        }}
                      >
                        {t("modals.redeem.info.fund")}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: "1.65rem",
                          lineHeight: "1",
                          fontWeight: "500",
                          color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                          marginTop: "5px",
                        }}
                      >
                        {activeRedeemFund?.fund_code}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: ".85rem",
                          fontWeight: "400",
                          lineHeight: "1",
                          color: theme === "dark" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                          marginTop: "3px",
                        }}
                      >
                        {activeRedeemFund?.fund_name}
                      </div>
                    </div>
                    <div
                      className='col-auto p-0 text-center'
                      style={{
                        minWidth: "140px",
                      }}
                    >
                      <div
                        className=''
                        style={{
                          fontSize: ".85rem",
                          fontWeight: "400",
                          letterSpacing: ".03em",
                          color: "rgb(170, 170, 170)",
                        }}
                      >
                        {t("portfolio.fund.units")}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: "1.65rem",
                          lineHeight: "1",
                          fontWeight: "500",
                          color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                          marginTop: "5px",
                        }}
                      >
                        {numberWithCommas(unitHoldings.whole)}
                        {unitHoldings.hasDecimal ? "." : ""}
                        {unitHoldings.hasDecimal ? <span style={{ fontSize: ".975rem" }}>{unitHoldings.decimal}</span> : ""}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: ".85rem",
                          fontWeight: "400",
                          lineHeight: "1",
                          color: theme === "dark" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                          marginTop: "3px",
                          minHeight: "13.6px",
                        }}
                      >
                        {t("app.fund.units")}
                      </div>
                    </div>
                    {/* <div
                                                className='col-auto p-0 text-center'
                                                style={{
                                                    minWidth: "140px",
                                                }}
                                            >
                                                <div
                                                    className=''
                                                    style={{
                                                        fontSize: ".85rem",
                                                        fontWeight: "400",
                                                        letterSpacing: ".03em",
                                                        color: "rgb(170, 170, 170)",
                                                    }}
                                                >
                                                    Price
                                                </div>
                                                <div
                                                    className=''
                                                    style={{
                                                        fontSize: "1.65rem",
                                                        lineHeight: "1",
                                                        fontWeight: "500",
                                                        color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                                                        marginTop: "5px",
                                                    }}
                                                >
                                                    <span className='me-1' style={{ fontSize: ".9rem", color: "rgb(111, 151, 200)" }}>
                                                        Rs
                                                    </span>
                                                    {format4(activeRedeemFund?.navData.nav)}
                                                </div>
                                                <div
                                                    className=''
                                                    style={{
                                                        fontSize: "0.85rem",
                                                        fontWeight: "400",
                                                        lineHeight: "1",
                                                        color: theme === "dark" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                                                        marginTop: "3px",
                                                    }}
                                                >
                                                    Per Share
                                                </div>
                                            </div> */}
                  </div>
                </div>
                <div className='col-12 p-0'>
                  <hr className='my-2 mx-1' style={{ color: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)" }} />
                </div>
                <div className='col p-0 pe-1'>
                  <div
                    className='d-flex flex-column align-items-center justify-content-center flex-nowrap mx-auto'
                    style={{
                      minHeight: "180px",
                      maxWidth: "380px",
                    }}
                  >
                    <div className='w-100 mx-auto mb-2'>
                      <div
                        className='text-center'
                        style={{
                          fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                          color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                          fontWeight: "400",
                          lineHeight: "1",
                        }}
                      >
                        {t("modals.redeem.info.amount")}
                      </div>
                      <input
                        type='text'
                        className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""} rounded-pill text-center`}
                        name='total_amount'
                        value={redeemData.total_amount && numberWithCommas(redeemData.total_amount)}
                        style={{
                          ...themeBasedPurchaseFormControl(theme),
                          marginTop: ".4rem",
                          fontSize: screenSize >= 576 ? "1.1rem" : "1rem",
                        }}
                        onChange={handleRedeemFundChange}
                      />
                    </div>
                    <div
                      className='text-center mb-2'
                      style={{
                        fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                        color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                        fontWeight: "400",
                        lineHeight: "1",
                        minHeight: "14px",
                      }}
                    >
                      {redeemData.total_amount ? amountToWords(redeemData.total_amount) : ""}
                    </div>
                    <div className='row w-100 mx-auto position-relative'>
                      <div className='col p-0 pe-1'>
                        <div
                          className='text-center'
                          style={{
                            fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                            color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                            fontWeight: "400",
                            lineHeight: "1",
                          }}
                        >
                          {t("app.fund.price")}
                        </div>
                        <input
                          type='text'
                          disabled
                          className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""} rounded-pill text-center`}
                          value={format4(activeRedeemFund?.navData?.redemption_price)}
                          style={{
                            ...themeBasedPurchaseFormControl(theme),
                            marginTop: ".4rem",
                            fontSize: screenSize >= 576 ? "1.1rem" : "1rem",
                          }}
                        />
                      </div>
                      <div className='col-auto p-0'>
                        <div className='d-flex flex-column justify-content-end align-items-center h-100 pb-2'>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width={26}
                            height={26}
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='var(--theme-light-primary)'
                            strokeWidth={1.5}
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='icon icon-tabler icons-tabler-outline icon-tabler-x'
                          >
                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                            <path d='M18 6l-12 12' />
                            <path d='M6 6l12 12' />
                          </svg>
                        </div>
                      </div>
                      <div className='col p-0 ps-1'>
                        <div
                          className='text-center d-flex align-items-center justify-content-center flex-nowrap'
                          style={{
                            fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                            color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                            fontWeight: "400",
                            lineHeight: "1",
                          }}
                        >
                          {t("modals.redeem.info.units")}

                          <div className='ms-3 p-0'>
                            <input
                              className='form-check-input m-0'
                              type='checkbox'
                              checked={redeemData.fund_units ? parseFloat(redeemData.fund_units) == activeRedeemFund?.fund_unit : false}
                              onChange={() => {
                                if (parseFloat(redeemData.fund_units) == activeRedeemFund?.fund_unit) {
                                  handleRedeemFundChange({ target: { name: "fund_units", value: "" } });
                                } else {
                                  handleRedeemFundChange({ target: { name: "fund_units", value: String(activeRedeemFund?.fund_unit || "") } });
                                }
                                // setConversionData(prev => ({ ...prev, allUnits: !prev.allUnits }))
                              }}
                              value='all'
                              id='checkDefault'
                              style={{
                                height: "13px",
                                width: "13px",
                              }}
                            />
                          </div>

                          <div className='ms-1'>All</div>
                        </div>
                        <input
                          type='text'
                          name='fund_units'
                          value={redeemData.fund_units ? numberWithCommas(redeemData.fund_units) : ""}
                          className={`form-control-with-error ${validationError ? "" : `goal-form-control${theme === "dark" ? "-dark" : ""}`} rounded-pill text-center`}
                          style={
                            validationError
                              ? {
                                  ...themeBasedPurchaseFormControl(theme),
                                  boxShadow: "0 0 2px .15rem rgba(192, 19, 31, 0.25)",
                                  borderColor: "rgb(192, 19, 31)",
                                  marginTop: ".4rem",
                                  fontSize: screenSize >= 576 ? "1.1rem" : "1rem",
                                }
                              : {
                                  ...themeBasedPurchaseFormControl(theme),
                                  // boxShadow: validationError ? '0 0 2px .15rem rgba(192, 19, 31, 0.25)' : '',
                                  marginTop: ".4rem",
                                  fontSize: screenSize >= 576 ? "1.1rem" : "1rem",
                                }
                          }
                          onChange={handleRedeemFundChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='modal-footer border-0 flex-nowrap justify-content-between'>
              <div
                className=''
                style={{
                  maxWidth: screenSize >= 576 ? "calc(100% - 300px)" : "65%",
                  fontSize: screenSize >= 576 ? ".775rem" : ".75rem",
                  lineHeight: "1.2",
                  color: theme === "light" ? "rgba(0,0,0,.6)" : "rgba(255,255,255,.5)",
                }}
              >
                {t("modals.redeem.info.caption")}
              </div>
              <button
                type='submit'
                disabled={loading.redeemFund}
                className='btn btn-primary border-0 px-4 pb-2 d-flex align-items-center justify-content-center'
                onClick={() => {}}
                style={{
                  minHeight: "38px",
                  minWidth: "80px",
                  fontSize: screenSize >= 576 ? "1rem" : ".9rem",
                }}
              >
                {loading.redeemFund ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm'
                      aria-hidden='true'
                      style={{
                        fontSize: "0.85rem",
                        height: screenSize >= 576 ? "15px" : "14px",
                        width: screenSize >= 576 ? "15px" : "14px",
                      }}
                    ></span>
                    {/* <span role="status">Loading...</span> */}
                  </>
                ) : (
                  t("modals.common.next")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* -- PAYMENT FUND - MODAL -- */}
      <div className='modal fade' id='paymentModal' tabIndex='-1' aria-labelledby='paymentModal' aria-hidden='true' ref={paymentModal}>
        <div
          className='modal-dialog modal-dialog-centered'
          style={{
            ["--bs-modal-width"]: "640px",
            marginInline: screenSize >= 656 ? "auto" : ".5rem",
          }}
        >
          <form
            className='modal-content'
            onSubmit={handleFundPayment}
            style={{
              backgroundColor: theme === "light" ? "rgb(243, 243, 243)" : "rgb(50, 50, 50)",
              border: "none",
            }}
          >
            <div
              className='modal-header align-items-center justify-content-between position-relative border-0'
              style={{
                backgroundColor: theme === "light" ? "rgb(173, 115, 176)" : "rgb(145, 78, 148)",
                minHeight: "67px",
                padding: screenSize >= 576 ? "1rem .8rem" : ".75rem .65rem",
              }}
            >
              <div
                className='ps-2'
                style={{
                  color: "rgba(255,255,255,.7)",
                  fontSize: screenSize >= 576 ? "1rem" : ".82rem",
                }}
              >
                {t("modals.common.step")} 2<span style={{ margin: "0 .125rem" }}>/</span>2
              </div>

              <h1
                className='modal-title fs-4 position-absolute start-50 translate-middle-x'
                id='goalModal'
                style={{ color: "rgb(224, 232, 240)", fontSize: screenSize >= 576 ? "1.45rem" : "1.25rem", lineHeight: screenSize >= 576 ? "" : "1.1" }}
              >
                {t("modals.redeem.payment.title")}
              </h1>
              <button
                type='button'
                className='btn-close p-0 d-flex align-items-center justify-content-center'
                aria-label='Close'
                style={{
                  marginLeft: "0",
                  marginRight: ".15rem",
                  color: "rgb(255,255,255)",
                  background: "none",
                  height: screenSize >= 576 ? "32px" : "28px",
                  width: screenSize >= 576 ? "32px" : "28px",
                  ["--bs-btn-close-opacity"]: "0.65",
                  ["--bs-btn-close-hover-opacity"]: "0.85",
                }}
                onClick={togglePaymentModal}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  height={screenSize >= 576 ? 18 : 16}
                  width={screenSize >= 576 ? 18 : 16}
                  stroke='currentColor'
                  fill='currentColor'
                  viewBox='0 0 16 16'
                >
                  <path d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414'></path>
                </svg>
              </button>
            </div>

            <hr className='m-0' />

            <div className='modal-body p-0'>
              <div className='row w-100 mx-auto gap-3 py-3 px-3 p-sm-3 px-sm-4 pb-sm-2'>
                <div className='col-12 p-0'>
                  <div className='row w-100 mx-auto py-2 px-1 row-gap-3'>
                    <div className='col-12 p-0'>
                      <div
                        className='form-label ps-1'
                        style={{
                          color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                          fontSize: ".85rem",
                          fontWeight: "400",
                          lineHeight: "1.1",
                          marginBottom: ".5rem",
                        }}
                      >
                        {t("modals.redeem.payment.message")}
                      </div>
                    </div>
                    <div className='col-12 p-0'>
                      <BankAccountCard
                        account={{
                          value: {
                            bank_name: unitholder?.bank_name,
                            branch_name: unitholder?.bank_name,
                            account_no: unitholder?.account_no,
                            account_title: unitholder?.account_title,
                          },
                        }}
                        selected={true}
                        onSelect={updateFundPaymentData}
                        isRedeem={true}
                      />
                    </div>
                    {/* <div className='col-12 col-sm p-0 pe-sm-3'>
                          <label
                            htmlFor='bank-name'
                            className='form-label ps-1'
                            style={{
                              color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                              fontSize: ".85rem",
                              fontWeight: "400",
                              lineHeight: "1.1",
                              marginBottom: ".5rem",
                            }}
                          >
                            Bank Name <sup style={{ color: "rgb(239, 112, 112)", fontSize: "15px", top: "-.25em" }}>*</sup>
                          </label>
                          <input
                            type='text'
                            name='bank_name'
                            value={fundPaymentData.bank_name}
                            onChange={updateFundPaymentData}
                            className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                            id='portfolio-name'
                            style={{
                              ...themeBasedPurchaseFormControl(theme),
                              // backgroundColor: 'rgb(238, 242, 246)',
                            }}
                          />
                        </div>
                        <div className='col-12 col-sm p-0 ps-sm-3'>
                          <label
                            htmlFor='branch_name'
                            className='form-label ps-1'
                            style={{
                              color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                              fontSize: ".85rem",
                              fontWeight: "400",
                              lineHeight: "1.1",
                              marginBottom: ".5rem",
                            }}
                          >
                            Branch Name <sup style={{ color: "rgb(239, 112, 112)", fontSize: "15px", top: "-.25em" }}>*</sup>
                          </label>
                          <input
                            type='text'
                            name='branch_name'
                            value={fundPaymentData.branch_name}
                            onChange={updateFundPaymentData}
                            className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                            id='portfolio-name'
                            style={{
                              ...themeBasedPurchaseFormControl(theme),
                              // backgroundColor: 'rgb(238, 242, 246)',
                            }}
                          />
                        </div>
                        <div className='col-12 p-0'>
                          <label
                            htmlFor='account_title'
                            className='form-label ps-1'
                            style={{
                              color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                              fontSize: ".85rem",
                              fontWeight: "400",
                              lineHeight: "1.1",
                              marginBottom: ".5rem",
                            }}
                          >
                            Account Title <sup style={{ color: "rgb(239, 112, 112)", fontSize: "15px", top: "-.25em" }}>*</sup>
                          </label>
                          <input
                            type='text'
                            name='account_title'
                            value={fundPaymentData.account_title}
                            onChange={updateFundPaymentData}
                            className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                            id='portfolio-name'
                            style={{
                              ...themeBasedPurchaseFormControl(theme),
                              // backgroundColor: 'rgb(238, 242, 246)',
                            }}
                          />
                        </div>
                        <div className='col-12 p-0'>
                          <label
                            htmlFor='iban'
                            className='form-label ps-1'
                            style={{
                              color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                              fontSize: ".85rem",
                              fontWeight: "400",
                              lineHeight: "1.1",
                              marginBottom: ".5rem",
                            }}
                          >
                            IBAN No. <sup style={{ color: "rgb(239, 112, 112)", fontSize: "15px", top: "-.25em" }}>*</sup>
                          </label>
                          <input
                            type='text'
                            name='iban'
                            value={fundPaymentData.iban}
                            onChange={updateFundPaymentData}
                            className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                            id='portfolio-name'
                            style={{
                              ...themeBasedPurchaseFormControl(theme),
                              // backgroundColor: 'rgb(238, 242, 246)',
                            }}
                          />
                        </div> */}
                    <div className='col-12 col-sm p-0 pe-sm-3'>
                      <label
                        htmlFor='amount'
                        className='form-label ps-1'
                        style={{
                          color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                          fontSize: ".85rem",
                          fontWeight: "400",
                          lineHeight: "1.1",
                          marginBottom: ".5rem",
                        }}
                      >
                        {t("modals.redeem.payment.amount")}{" "}
                      </label>
                      <input
                        type='text'
                        name='amount'
                        disabled={true}
                        value={redeemData.total_amount ? numberWithCommas(redeemData.total_amount) : ""}
                        className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                        id='portfolio-name'
                        style={{
                          ...themeBasedPurchaseFormControl(theme),
                          // backgroundColor: 'rgb(238, 242, 246)',
                        }}
                      />
                    </div>
                    <div className='col-12 col-sm p-0 ps-sm-3'>
                      <label
                        htmlFor='fund_units'
                        className='form-label ps-1'
                        style={{
                          color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                          fontSize: ".85rem",
                          fontWeight: "400",
                          lineHeight: "1.1",
                          marginBottom: ".5rem",
                        }}
                      >
                        {t("modals.redeem.payment.units")} <sup style={{ color: "rgb(239, 112, 112)", fontSize: "15px", top: "-.25em" }}>*</sup>
                      </label>
                      <input
                        type='text'
                        name='fund_units'
                        value={redeemData.fund_units ? numberWithCommas(redeemData.fund_units) : ""}
                        onChange={handleRedeemFundChange}
                        className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                        id='portfolio-name'
                        style={{
                          ...themeBasedPurchaseFormControl(theme),
                          // backgroundColor: 'rgb(238, 242, 246)',
                        }}
                      />
                    </div>
                    <div className='col-12 p-0'>
                      <label
                        htmlFor='comment'
                        className='form-label ps-1'
                        style={{
                          color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                          fontSize: ".85rem",
                          fontWeight: "400",
                          lineHeight: "1.1",
                          marginBottom: ".5rem",
                        }}
                      >
                        {t("modals.redeem.payment.comment")}
                      </label>
                      <textarea
                        type='text'
                        name='comment'
                        value={fundPaymentData.comment}
                        onChange={updateFundPaymentData}
                        className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                        id='portfolio-name'
                        style={{
                          ...themeBasedPurchaseFormControl(theme),
                          // backgroundColor: 'rgb(238, 242, 246)',
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className='col-12 p-0'>
                  <hr className='my-2 mx-1' style={{ color: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)" }} />
                </div>
              </div>
            </div>

            <div className='modal-footer border-0 flex-nowrap flex-column flex-sm-row justify-content-between gap-2 gap-sm-0'>
              <div
                className=''
                style={{
                  maxWidth: screenSize >= 576 ? "calc(100% - 300px)" : "100%",
                  fontSize: screenSize >= 576 ? ".775rem" : ".75rem",
                  lineHeight: "1.2",
                  color: theme === "light" ? "rgba(0,0,0,.6)" : "rgba(255,255,255,.5)",
                }}
              >
                {t("modals.redeem.payment.caption")}
              </div>
              <button
                type='submit'
                disabled={loading.paymentFund}
                className='btn btn-primary border-0 px-4 pb-2'
                style={{
                  minWidth: "80px",
                }}
              >
                {loading.paymentFund ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm'
                      aria-hidden='true'
                      style={{
                        fontSize: "0.85rem",
                        height: "15px",
                        width: "15px",
                      }}
                    ></span>
                    {/* <span role="status">Loading...</span> */}
                  </>
                ) : (
                  t("modals.common.submit")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
